import React, { useRef, useState } from "react";
import CustomInput from "./CustomInput";
import EmptyTable from "./EmptyTable";
import CustomSelect from "./CustomSelect";

export type Column<T> = {
  label: string | React.ReactNode;
  accessor: keyof T | string;
  render?: (item: T, index: number) => React.ReactNode;
  searchable?: boolean;
  searchType?: "text" | "date" | "select";
  searchOptions?: string[];
};

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  searchFilters?: Record<string, string>;
  onSearchChange?: (accessor: string, value: string) => void;
  onSearchSubmit?: (filters: Record<string, string>) => void;
  rowClassName?: (item?: T, index?: number) => string;
  isLoading?: boolean;
}

export default function DataTable<T>({
  columns,
  data,
  searchFilters = {},
  onSearchChange,
  rowClassName,
  isLoading = false,
}: DataTableProps<T>) {
  const hasSearchableColumns = columns.some((col) => col.searchable);
  const [localSearchFilters, setLocalSearchFilters] = useState(searchFilters);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (accessor: string, value: string) => {
    const newFilters = { ...localSearchFilters, [accessor]: value };
    setLocalSearchFilters(newFilters);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      onSearchChange?.(accessor, value);
    }, 1000);
  };

  const isFiltering = Object.values(localSearchFilters).some(
    (v) => String(v ?? "").trim() !== "",
  );

  const SkeletonRow = () => (
    <tr>
      {columns.map((_, idx) => (
        <td key={idx} className="px-4 py-3">
          <div className="h-4 w-full bg-background-600 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="w-full overflow-x-auto custom-scrollbar">
      <table className="min-w-full w-max">
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th
                key={idx}
                className="py-3 px-4 text-left align-top whitespace-nowrap font-semibold text-[13px] tracking-wide capitalize text-neutral-300"
              >
                {col.label}
              </th>
            ))}
          </tr>

          {hasSearchableColumns && (
            <tr>
              {columns.map((col, idx) => (
                <td key={idx} className="py-2 px-4">
                  {col.searchable && (
                    <>
                      {col.searchType === "select" ? (
                        <CustomSelect
                          name={`search-${String(col.accessor)}`}
                          value={
                            localSearchFilters[col.accessor as string] || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              col.accessor as string,
                              e.target.value,
                            )
                          }
                          options={col.searchOptions || []}
                          placeholder="Select"
                          selectClassName="w-36 border-none bg-background-200"
                        />
                      ) : (
                        <CustomInput
                          name={`search-${String(col.accessor)}`}
                          type={col.searchType || "search"}
                          placeholder="Search..."
                          value={
                            localSearchFilters[col.accessor as string] || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              col.accessor as string,
                              e.target.value,
                            )
                          }
                          inputClassName="w-36 pl-12 border-none bg-background-200"
                          isSearchButton
                        />
                      )}
                    </>
                  )}
                </td>
              ))}
            </tr>
          )}
        </thead>

        <tbody>
          {isLoading ? (
            <>
              {[...Array(5)].map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </>
          ) : data.length > 0 ? (
            data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={`hover:bg-background-300 cursor-pointer transition-colors ${
                  rowClassName ? rowClassName(item, rowIndex) : ""
                }`}
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-4 py-4 text-sm font-normal text-neutral-400 align-top"
                  >
                    {col.render
                      ? col.render(item, rowIndex)
                      : (item as any)[col.accessor]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-6 text-center">
                {isFiltering ? (
                  <div className="p-5">No records available.</div>
                ) : (
                  <EmptyTable />
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
